import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldAlert, Gavel, Send, Clock, CheckCircle2, XCircle, LogIn, Scale } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

/* Ban Appeal Kiosk — a website page (studio styling, no game boot). Players banned
   from New Citizen at the Steam level sign in with the banned account, plead their
   case once per cooldown window, and watch the verdict land here. Studio staff
   review the queue in Admin → Ban Appeals; approving lifts the global ban. */
export default function AppealPage() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    if (!localStorage.getItem("nc_token")) { setAuthed(false); setReady(true); return; }
    api.get("/nc/appeals/mine")
      .then((r) => { setAuthed(true); setState(r.data); })
      .catch(() => { setAuthed(false); })
      .finally(() => setReady(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const steamSignin = () => {
    window.location.href = `/api/auth/steam/login?next=${encodeURIComponent("/appeal")}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post("/nc/appeals", { text });
      toast.success(r.data.message);
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const wrap = (children) => (
    <div data-testid="appeal-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 grid-texture opacity-40" />
      <motion.div className="relative w-full max-w-2xl flex flex-col gap-6" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
        <div className="text-center">
          <p className="font-mono text-[9px] tracking-[0.5em] text-zinc-500 uppercase">Specpixel Entertainment</p>
          <h1 className="font-sans font-bold uppercase tracking-tight text-2xl md:text-3xl text-zinc-50 mt-2 flex items-center justify-center gap-3">
            <Scale className="w-6 h-6 text-nc-orange" /> Ban Appeal Kiosk
          </h1>
          <p className="font-mono text-[10px] text-zinc-500 mt-2 tracking-[0.15em] uppercase">New Citizen · global game bans</p>
        </div>
        {children}
        <p className="text-center font-mono text-[9px] text-zinc-600 tracking-[0.2em] uppercase">
          Website account ban instead? <Link to="/support" className="text-nc-orange underline">Use the support desk</Link>
        </p>
      </motion.div>
    </div>
  );

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase animate-pulse">Loading…</p>
      </div>
    );
  }

  if (!authed) {
    return wrap(
      <div data-testid="appeal-signin" className="border border-white/10 bg-black/40 p-8 text-center">
        <LogIn className="w-8 h-8 text-nc-orange mx-auto mb-4" />
        <p className="font-mono text-xs text-zinc-300 leading-relaxed mb-6">
          Sign in with the <span className="text-zinc-50">Steam account that received the ban</span> so we can pull up your case.
        </p>
        <button data-testid="appeal-steam-signin" onClick={steamSignin}
          className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-3 transition-colors">
          Sign in with Steam
        </button>
      </div>
    );
  }

  if (state?.steam_required) {
    return wrap(
      <div data-testid="appeal-steam-required" className="border border-amber-500/30 bg-amber-500/5 p-8 text-center">
        <p className="font-mono text-xs text-zinc-300 leading-relaxed mb-6">
          This website account has no Steam profile linked. Appeals must come from the banned Steam account.
        </p>
        <button data-testid="appeal-steam-link" onClick={steamSignin}
          className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-3 transition-colors">
          Link via Steam sign-in
        </button>
      </div>
    );
  }

  if (!state?.banned) {
    return wrap(
      <div data-testid="appeal-good-standing" className="border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
        <p className="font-sans font-bold uppercase text-lg text-zinc-50">Account in good standing</p>
        <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed">
          This Steam account has no active New Citizen ban. See you in the city.
        </p>
      </div>
    );
  }

  const open = state.open_appeal;
  const latest = state.latest;

  return wrap(
    <>
      <div className="border border-red-500/40 bg-red-500/5 p-6 md:p-8">
        <div className="flex items-center gap-4 mb-4">
          <span className="w-12 h-12 border border-red-500/50 bg-red-500/10 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-6 h-6 text-red-400" />
          </span>
          <div>
            <p className="font-mono text-[9px] tracking-[0.4em] text-red-400 uppercase">Globally banned from New Citizen</p>
            <p data-testid="appeal-ban-reason" className="font-mono text-xs text-zinc-200 mt-1.5 leading-relaxed">{state.ban_reason}</p>
          </div>
        </div>
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
          This is a studio-issued permanent ban covering every New Citizen server. Appeals are read by a human —
          be honest and specific. If an appeal is denied you may try again after {7} days.
        </p>
      </div>

      {open && (
        <div data-testid="appeal-status-pending" className="border border-nc-orange/40 bg-nc-orange/5 p-6">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-5 h-5 text-nc-orange" />
            <h2 className="font-sans font-bold uppercase text-base text-zinc-50">Appeal under review</h2>
          </div>
          <p className="font-mono text-xs text-zinc-400 leading-relaxed">Filed {String(open.created_at).slice(0, 10)} — the outcome will appear on this page.</p>
          <p className="font-mono text-xs text-zinc-300 leading-relaxed mt-3 border-t border-white/10 pt-3">"{open.text}"</p>
        </div>
      )}

      {!open && latest?.status === "approved" && (
        <div data-testid="appeal-status-approved" className="border border-emerald-500/40 bg-emerald-500/5 p-6">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <h2 className="font-sans font-bold uppercase text-base text-zinc-50">Appeal approved — ban lifted</h2>
          </div>
          {latest.review_note && <p className="font-mono text-xs text-zinc-400">Staff note: {latest.review_note}</p>}
          <p className="font-mono text-[10px] text-zinc-500 mt-2 uppercase tracking-[0.15em]">Welcome back. Don't make us regret it.</p>
        </div>
      )}

      {!open && latest?.status === "denied" && (
        <div data-testid="appeal-status-denied" className="border border-red-500/30 bg-red-500/5 p-6">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="w-5 h-5 text-red-400" />
            <h2 className="font-sans font-bold uppercase text-base text-zinc-50">Previous appeal denied</h2>
          </div>
          {latest.review_note && <p className="font-mono text-xs text-zinc-400">Staff note: {latest.review_note}</p>}
          {state.cooldown_until && (
            <p data-testid="appeal-cooldown" className="font-mono text-[10px] text-zinc-500 mt-2 uppercase tracking-[0.15em]">
              You can appeal again after {state.cooldown_until.slice(0, 10)}
            </p>
          )}
        </div>
      )}

      {state.can_appeal && (
        <motion.form data-testid="appeal-form" onSubmit={submit} className="border border-white/10 bg-black/40 p-6 md:p-8 flex flex-col gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}>
          <h2 className="font-sans font-bold uppercase text-base text-zinc-50 flex items-center gap-2">
            <Gavel className="w-4 h-4 text-nc-orange" /> Your appeal
          </h2>
          <textarea data-testid="appeal-text-input" required minLength={50} maxLength={2000} rows={6} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="What happened, why the ban should be lifted, and what changes going forward. Minimum 50 characters — vague appeals are declined."
            className="w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange resize-none" />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="font-mono text-[9px] text-zinc-600 tracking-[0.15em] uppercase">{text.length}/2000 · min 50</p>
            <button data-testid="appeal-submit-button" type="submit" disabled={sending || text.trim().length < 50}
              className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-50 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors">
              <Send className="w-4 h-4" /> {sending ? "Submitting…" : "Submit Appeal"}
            </button>
          </div>
        </motion.form>
      )}
    </>
  );
}
